import { FormHelperText, Stack, TextField } from '@mui/material'
import { formHelperTextClasses } from '@mui/material/FormHelperText'
import React from 'react'
import {
  get,
  RegisterOptions,
  useController,
  UseFormReturn,
} from 'react-hook-form'
import { ErrorIcon } from '../icons'

interface InputFieldProps {
  required?: boolean
  name: string
  form?: UseFormReturn<any, any>
  style?: {
    height?: number
    opacity?: number
    borderRadius?: string
    width?: {
      xs?: string | number
    }
    marginTop?: string
  }
  padding?: string
  styleInput?: {
    backgroundColor?: string
  }
  rules?: Exclude<
    RegisterOptions,
    'valueAsNumber' | 'valueAsDate' | 'setValueAs'
  >
  type?: string | undefined
  placeholder?: any
  label?: string
  autoFocus?: boolean
  inputRef?: any
  disabled?: boolean
  isRequired?: boolean
  value?: any
  onChange?: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>
  backgroundColor?: string
  error?: boolean
  helperText?: string
  hiddenLabel?: boolean
  numbersOnly?: boolean
  allowDecimal?: boolean
  allowNegative?: boolean
  maxLength?: number
}

const InputLabelField = React.forwardRef<HTMLInputElement, InputFieldProps>(
  (
    {
      isRequired,
      required,
      name,
      form,
      placeholder,
      label,
      padding,
      type,
      style,
      inputRef,
      disabled,
      value,
      onChange,
      backgroundColor,
      hiddenLabel,
      numbersOnly,
      allowDecimal,
      allowNegative,
      styleInput,
      maxLength,
      error,
      helperText,
      ...props
    },
    ref,
  ) => {
    const { control } = form || { control: null }

    const {
      field,
      formState: { errors },
    } = control
      ? // eslint-disable-next-line react-hooks/rules-of-hooks
        useController({ name, control })
      : {
          field: { onChange, value, onBlur: undefined, ref: undefined },
          formState: { errors: undefined },
        }

    const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (!numbersOnly) return

      const key = event.key

      const controlKeys =
        /^(Backspace|Delete|Tab|Escape|Enter|Home|End|PageUp|PageDown|ArrowLeft|ArrowRight|ArrowUp|ArrowDown|Insert|F1|F2|F3|F4|F5|F6|F7|F8|F9|F10|F11|F12)$/
      if (controlKeys.test(key)) return

      if (event.ctrlKey || event.metaKey) {
        const allowedCtrlKeys = /^[aAcCvVxXzZyY]$/
        if (allowedCtrlKeys.test(key)) return
      }

      const numberPattern = /^[0-9]$/
      if (numberPattern.test(key)) return

      if (allowDecimal && (key === '.' || key === 'Decimal')) {
        const currentValue = event.currentTarget.value
        if (!currentValue.includes('.')) return
      }
      if (allowNegative && (key === '-' || key === 'Subtract')) {
        const selectionStart = event.currentTarget.selectionStart
        if (selectionStart === 0 && !event.currentTarget.value.includes('-'))
          return
      }
      event.preventDefault()
    }

    const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
      if (!numbersOnly) return

      event.preventDefault()
      const paste = event.clipboardData.getData('text')

      let filteredPaste = paste.replace(/[^0-9]/g, '')

      if (allowDecimal) {
        const parts = paste.split('.')
        if (parts.length > 1) {
          filteredPaste =
            parts[0].replace(/[^0-9]/g, '') +
            '.' +
            parts
              .slice(1)
              .join('')
              .replace(/[^0-9]/g, '')
        }
      }

      if (allowNegative && paste.startsWith('-')) {
        filteredPaste = '-' + filteredPaste
      }
      const target = event.target as HTMLInputElement
      const start = target.selectionStart || 0
      const end = target.selectionEnd || 0
      const currentValue = target.value
      const newValue =
        currentValue.slice(0, start) + filteredPaste + currentValue.slice(end)

      const syntheticEvent = {
        target: {
          ...target,
          value: newValue,
        },
        currentTarget: {
          ...target,
          value: newValue,
        },
      } as React.ChangeEvent<HTMLInputElement>
      if (field.onChange) {
        field.onChange(syntheticEvent)
      }
    }
    const fieldError =
      get(errors, name) ||
      (error || helperText
        ? { message: helperText || (typeof error === 'string' ? error : '') }
        : undefined)

    return (
      <Stack
        sx={{
          ...(disabled && {
            opacity: 0.7,
            pointerEvents: 'none',
            userSelect: 'none',
          }),
        }}
      >
        <TextField
          required={required}
          fullWidth
          id={name}
          label={label}
          placeholder={placeholder}
          inputRef={inputRef || ref}
          variant="filled"
          type={type}
          value={field.value ?? ''}
          inputProps={{
            ...field,
            value: field.value ?? '',
            ...(maxLength ? { maxLength } : {}),
          }}
          InputProps={{ disableUnderline: true }}
          onChange={field.onChange}
          onKeyDown={handleKeyPress}
          onPaste={handlePaste}
          autoComplete="off"
          sx={{
            gap: '4px',
            lineHeight: '115%',
            color: !!fieldError ? 'text.main' : '#FF3030',
            borderColor: !!fieldError ? '#FF3030' : '#F1F1F1',
            backgroundColor: !!fieldError
              ? 'error.main'
              : 'background.overrideDefault',
            '&:hover': {
              borderColor: !!fieldError ? '#FF3030' : '#004F4B',
              backgroundColor: !!fieldError
                ? 'error.main'
                : 'background.overrideDefault',
            },
            [`& .MuiInputLabel-root`]: {
              color: !!fieldError ? 'primary.contrastText' : 'text.main',
              lineHeight: '16px',
              fontWeight: '700',
              fontSize: '12px',
              left: '4px',
            },
            [`& .Mui-disabled`]: {
              color: 'text.main',
              backgroundColor: !!fieldError
                ? 'error.main'
                : 'background.overrideDefault',
              borderRadius: 'inherit',
            },
            ...(isRequired && {
              [`& .MuiInputLabel-root::after`]: {
                content: '"*"',
                color: '#ED2121',
                fontFamily: 'inherit',
                verticalAlign: 'middle',
                fontSize: '14px',
              },
            }),
            [`& .MuiInputLabel-shrink.Mui-focused`]: {
              color: !!fieldError ? 'primary.contrastText' : 'text.main',
            },

            [`& .MuiInputBase-root`]: {
              color: 'text.main',
              borderBottomLeftRadius: style?.borderRadius,
              borderBottomRightRadius: style?.borderRadius,
              borderTopRightRadius: style?.borderRadius,
              borderTopLeftRadius: style?.borderRadius,
              backgroundColor: !!fieldError
                ? 'error.main'
                : backgroundColor
                  ? backgroundColor
                  : 'background.overrideDefault',
            },
            [`& .MuiInputBase-input`]: {
              borderBottomLeftRadius: style?.borderRadius,
              borderBottomRightRadius: style?.borderRadius,
            },

            [`& .MuiFilledInput-input`]: {
              maxHeight: '17px',
              paddingLeft: '16px',
              ...styleInput,
            },
            borderRadius: '8px',
            width: style?.width?.xs,
            ...style,
          }}
          InputLabelProps={{
            style: hiddenLabel ? { display: 'none' } : {}, // Ẩn label bằng CSS
          }}
          {...props}
        />
        {fieldError?.message && (
          <Stack direction="row" spacing="5px" mt="10px" color="#FF3030">
            <ErrorIcon />
            <FormHelperText
              id="component-helper-text"
              error
              sx={{
                width: '1',
                fontSize: '12px',
                fontWeight: '400',
                lineHeight: '115%',
                flex: 'none',
                letterSpacing: '0.045em',
                [`&.${formHelperTextClasses.error}`]: {
                  color: '#FF3030',
                  marginLeft: '9px',
                  marginTop: '2px',
                  maxWidth: 'calc(100% - 20px)',
                },
              }}
            >
              {fieldError?.message as string}
            </FormHelperText>
          </Stack>
        )}
      </Stack>
    )
  },
)

InputLabelField.displayName = 'InputLabelField'

export default InputLabelField
